import React, { useEffect, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { ModernMultiValueWidget } from "./ModernMultiValueWidget";
import { ModernStatusChart } from "./ModernStatusChart";
import { ModernAttendanceChart } from "./ModernAttendanceChart";
import { ModernRevenueChart } from "./ModernRevenueChart";
import { TreatmentsByBranchChart } from "./TreatmentsByBranchChart";
import { TreatmentsByDealerChart } from "./TreatmentsByDealerChart";
import { JobCardsByBranchChart } from "./JobCardsByBranchChart";
import { PredictiveAnalyticsCard } from "./PredictiveAnalyticsCard";
import { ActivityHeatMapCard } from "./ActivityHeatMapCard";
import { WaterWashCard } from "./WaterWashCard";
import { JobsActivityCard } from "./JobsActivityCard";
import { DashboardStatsTable } from "./DashboardStatsTable";
import { JobCardsAndTreatmentsTabbedWidget } from "./JobCardsAndTreatmentsTabbedWidget";
import { getAdminDashboard, getAdminTableMetrics } from "../generic/crud";
import { JobCardDetailsDialog } from "../jobCards/JobCardDetailsDialog";
import { CacheTimerBadge } from "./CacheTimerBadge";
import {
  useThemeMode,
  systemMode,
} from "../../../_metronic/partials/layout/theme-mode/ThemeModeProvider";

export const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const isDarkMode =
    mode === "dark" || (mode === "system" && systemMode === "dark");
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [tableMetrics, setTableMetrics] = useState<any>(null);
  const [tableMetricsLoading, setTableMetricsLoading] = useState(true);
  const [countdown, setCountdown] = useState<number>(0);

  // Dialog state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsJobCardId, setDetailsJobCardId] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
    fetchTableMetrics();
  }, [refreshCounter]);

  // Countdown timer for cache expiry
  useEffect(() => {
    if (!dashboardData?.cacheExpiry) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const expiry = new Date(dashboardData.cacheExpiry).getTime();
      const remaining = Math.max(0, Math.floor((expiry - now) / 1000));
      setCountdown(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [dashboardData?.cacheExpiry]);

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await getAdminDashboard();
      setDashboardData(response.data);
      console.log("Dashboard data loaded");
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchTableMetrics = async () => {
    setTableMetricsLoading(true);
    try {
      const response = await getAdminTableMetrics();
      setTableMetrics(response.data);
    } catch (error) {
      console.error('Error fetching table metrics:', error);
      setTableMetrics(null);
    } finally {
      setTableMetricsLoading(false);
    }
  };

  const handleJobClick = (job: any) => {
    setDetailsJobCardId(job.id);
    setShowDetailsModal(true);
  };

  const handleRefresh = () => {
    setRefreshCounter((prev) => prev + 1);
  };

  // Get current month name and date range
  const today = new Date();
  const monthName = today.toLocaleString('en-US', { month: 'long' });
  const year = today.getFullYear();
  const firstDay = 1;
  const currentDay = today.getDate();
  const monthDateRange = `${monthName} ${firstDay}-${currentDay}, ${year}`;

  // Extract pre-computed values from dashboardData (with safe defaults)
  const todayRevenue = dashboardData?.todayRevenue || { customerPrice: 0, dealerPrice: 0, avgJobValue: 0, jobCount: 0 };
  const monthlyRevenue = dashboardData?.monthlyRevenue || { customerPrice: 0, dealerPrice: 0, avgJobValue: 0, jobCount: 0 };
  const pipelineRevenue = dashboardData?.pipelineRevenue || { customerPrice: 0, dealerPrice: 0, avgJobValue: 0, jobCount: 0 };

  return (
    <>
      {/* Header with Refresh Button */}
      <div className="d-flex justify-content-between align-items-center mb-6">
        <div>
          <h1 className="text-gray-900 fw-bolder fs-1 mb-2">Admin Dashboard</h1>
          <span className="text-muted fw-bold fs-6">
            Complete overview of operations and financials
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          {dashboardData && countdown > 0 && (
            <CacheTimerBadge countdown={countdown} formatCountdown={formatCountdown} />
          )}
          <Link
            to="/jobCards"
            className="btn btn-sm btn-primary d-none d-md-inline-block"
          >
            <i className="bi bi-card-checklist me-2"></i>
            Create Job
          </Link>
          <Link
            to="/attendance/branches"
            className="btn btn-sm btn-light-primary d-none d-md-inline-block"
          >
            <i className="bi bi-calendar-check me-2"></i>
            Attendance
          </Link>
          <button
            className="btn btn-sm btn-light-primary"
            onClick={handleRefresh}
            disabled={loading}
          >
            <i
              className={`bi bi-arrow-clockwise me-2 ${
                loading ? "spinner-border spinner-border-sm" : ""
              }`}
            ></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Row 1: 4 Consolidated KPI Cards */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        {/* Card 1: Job Cards & Treatments (Tabbed) */}
        <div className="col-12 col-md-6 col-xl-3">
          <JobCardsAndTreatmentsTabbedWidget
            openJobs={dashboardData?.openJobs || 0}
            inProgressJobs={dashboardData?.inProgressJobs || 0}
            closedTodayJobs={dashboardData?.closedToday || 0}
            closedLast7DaysJobs={dashboardData?.closedLast7Days || 0}
            treatmentStats={{
              todo: dashboardData?.treatmentsTodo || 0,
              inProgress: dashboardData?.treatmentsInProgress || 0,
              completed: dashboardData?.treatmentsCompleted || 0,
            }}
            onClick={() => navigate('/activeJobs')}
          />
        </div>

        {/* Card 2: Today's Revenue */}
        <div className="col-12 col-md-6 col-xl-3">
          <ModernMultiValueWidget
            title="Today's Revenue"
            icon="bi-cash-stack"
            iconColor="#50cd89"
            metrics={[
              {
                label: "Customer Price",
                value: `₹${(todayRevenue.customerPrice || 0).toLocaleString()}`,
              },
              {
                label: "Dealer Price",
                value: `₹${(todayRevenue.dealerPrice || 0).toLocaleString()}`,
              },
              {
                label: "Avg Job Value",
                value: `₹${(todayRevenue.avgJobValue || 0).toLocaleString()}`,
              },
              { label: "Jobs Closed", value: todayRevenue.jobCount || 0 },
            ]}
          />
        </div>

        {/* Card 3: Monthly Revenue */}
        <div className="col-12 col-md-6 col-xl-3">
          <ModernMultiValueWidget
            title={`${monthName} Revenue`}
            icon="bi-calendar2-check"
            iconColor="#7239ea"
            metrics={[
              {
                label: "Customer Price",
                value: `₹${(monthlyRevenue.customerPrice || 0).toLocaleString()}`,
              },
              {
                label: "Dealer Price",
                value: `₹${(monthlyRevenue.dealerPrice || 0).toLocaleString()}`,
              },
              {
                label: "Avg Job Value",
                value: `₹${(monthlyRevenue.avgJobValue || 0).toLocaleString()}`,
              },
              { label: "Jobs Closed", value: monthlyRevenue.jobCount || 0 },
            ]}
          />
        </div>

        {/* Card 4: Pipeline Revenue */}
        <div className="col-12 col-md-6 col-xl-3">
          <ModernMultiValueWidget
            title="Pipeline Revenue"
            icon="bi-graph-up-arrow"
            iconColor="#ffc700"
            metrics={[
              {
                label: "Customer Price",
                value: `₹${(pipelineRevenue.customerPrice || 0).toLocaleString()}`,
              },
              {
                label: "Dealer Price",
                value: `₹${(pipelineRevenue.dealerPrice || 0).toLocaleString()}`,
              },
              {
                label: "Avg Job Value",
                value: `₹${(pipelineRevenue.avgJobValue || 0).toLocaleString()}`,
              },
              { label: "Active Jobs", value: pipelineRevenue.jobCount || 0 },
            ]}
          />
        </div>
      </div>

      {/* Row 2: Quick Actions */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10 d-md-none">
        <div className="col-6">
          <Link to="/jobCards" className="card h-100 bg-light-primary hover-elevate-up" style={{textDecoration: 'none', border: '2px solid #009ef7'}}>
            <div className="card-body d-flex flex-column justify-content-center align-items-center text-center p-5 p-md-9">
                <i className="bi bi-card-checklist fs-2x fs-md-3x text-primary mb-3 mb-md-5"></i>
                <div className="fs-4 fs-md-2 fw-bolder text-primary mb-1 mb-md-2">Create Job</div>
                <div className="fw-bold text-primary opacity-75 d-none d-md-block">Assign new tasks</div>
            </div>
          </Link>
        </div>
        <div className="col-6">
          <Link to="/attendance/branches" className="card h-100 bg-light-success hover-elevate-up" style={{textDecoration: 'none', border: '2px solid #50cd89'}}>
            <div className="card-body d-flex flex-column justify-content-center align-items-center text-center p-5 p-md-9">
                <i className="bi bi-calendar-check fs-2x fs-md-3x text-success mb-3 mb-md-5"></i>
                <div className="fs-4 fs-md-2 fw-bolder text-success mb-1 mb-md-2">Attendance</div>
                <div className="fw-bold text-success opacity-75 d-none d-md-block">Track presence</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Row 3: Water Wash, Jobs Activity & Activity Heat Map */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-xl-4">
          <WaterWashCard
            className="h-100"
            today={dashboardData?.waterWashToday || 0}
            thisWeek={dashboardData?.waterWashThisWeek || 0}
            thisMonth={dashboardData?.waterWashThisMonth || 0}
          />
        </div>
        <div className="col-xl-4">
          <JobsActivityCard
            className="h-100"
            createdToday={dashboardData?.jobsCreatedToday || 0}
            closedToday={dashboardData?.jobsClosedToday || 0}
            createdYesterday={dashboardData?.jobsCreatedYesterday || 0}
            closedYesterday={dashboardData?.jobsClosedYesterday || 0}
            loading={loading}
          />
        </div>
        <div className="col-xl-4">
          <ActivityHeatMapCard
            className="h-100"
            heatmap={dashboardData?.heatmap || []}
            loading={loading}
          />
        </div>
      </div>

      {/* Row 4: Revenue, Status & Attendance */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-xl-4">
          <ModernRevenueChart
            revenueByBranch={dashboardData?.revenueByBranch || []}
            loading={loading}
          />
        </div>
        <div className="col-xl-4">
          <ModernStatusChart
            statusData={dashboardData?.statusChart || { open: 0, inProgress: 0 }}
            loading={loading}
          />
        </div>
        <div className="col-xl-4">
          <ModernAttendanceChart
            attendanceByBranch={dashboardData?.attendanceByBranch || []}
            loading={loading}
          />
        </div>
      </div>

      {/* Row 5: Treatment & Job Card Statistics Charts */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-xl-4">
          <TreatmentsByBranchChart
            treatmentStats={{ byBranch: dashboardData?.treatmentsByBranch || {} }}
            loading={loading}
          />
        </div>
        <div className="col-xl-4">
          <TreatmentsByDealerChart
            treatmentStats={{ byDealer: dashboardData?.treatmentsByDealer || {} }}
            loading={loading}
          />
        </div>
        <div className="col-xl-4">
          <JobCardsByBranchChart
            jobsByBranch={dashboardData?.jobsByBranch || []}
            loading={loading}
          />
        </div>
      </div>

      {/* Row 6: Dashboard Stats Table */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-xl-12">
            <DashboardStatsTable
              metrics={tableMetrics}
              loading={tableMetricsLoading}
            />
        </div>
      </div>

      {/* Row 7: Predictive Analytics */}
      <div className="mb-5 mb-xl-10">
        <PredictiveAnalyticsCard
          predictiveAnalytics={dashboardData?.predictiveAnalytics || {}}
          loading={loading}
        />
      </div>

      {/* Job Details Dialog */}
      <JobCardDetailsDialog
        show={showDetailsModal}
        handleClose={() => setShowDetailsModal(false)}
        jobCardId={detailsJobCardId}
        onStatusChange={handleRefresh}
      />
    </>
  );
};
